import type * as H from "history";
import { useState } from "react";

import {
    type IPreviousLocation,
    type IPreviousLocationState,
    type IRouteLocationState,
} from "./usePreviousLocation.types";

/**
 * isPreviousLocation checks if the given object is a previous location object.
 * @param to The object to check.
 * @returns True if the object is a previous location object, false otherwise.
 */
export function isPreviousLocation(
    to:
        | H.LocationDescriptor<unknown>
        | ((location: H.Location<unknown>) => H.LocationDescriptor<unknown>),
) {
    return (
        typeof to !== "string" &&
        typeof to === "object" &&
        to !== null &&
        "pathname" in to &&
        "state" in to
    );
}

/**
 * Determines if a route is a RouteLocationState object. This is used to determine if a route is a RouteLocationState object or a string path.
 * @param route the route to check if it is a RouteLocationState object
 * @returns true if the route is a RouteLocationState object, false otherwise
 */
export function isRouteLocationState(
    route:
        | H.LocationDescriptor<unknown>
        | ((location: H.Location<unknown>) => H.LocationDescriptor<unknown>),
): route is IRouteLocationState {
    return isPreviousLocation(route) && "previous" in (route as IRouteLocationState).state;
}

/**
 * usePreviousLocation is a custom hook that returns the previous location object from the history state.
 * @returns The previous location object or undefined if it does not exist.
 * IMPORTANT This hook cannot be used unless the page is navigated to using a job.routes.ts -> [createRoute](\repos\BTNet\Clients.App\src\utilities\flatRoutes\job.routes.ts) supported route.
 */
export function usePreviousLocation(): IPreviousLocation | undefined {
    const history = window.history;

    const [locState] = useState<IPreviousLocationState | undefined>(history.state?.state);

    return locState?.previous;
}

/**
 * getPreviousPathname returns the previous pathname without the prefix.
 * @param removePrefix The prefix to remove from the pathname.
 * @returns The previous pathname without the prefix.
 */
export function getPreviousPathname(removePrefix: RegExp = /^\/app/) {
    const loc = window.location;
    const locPath = loc.pathname.replace(removePrefix, "");
    return locPath + (loc.search ? `?${loc.search}` : "");
}

/**
 * getPreviousTitle returns the previous title. The title is the first part of the pathname. The first letter is capitalized.
 * @param removePrefix The prefix to remove from the pathname.
 * @param idMatch The regular expression to match the builder id. *
 * @param titleMatch The regular expression to match the title.
 * @returns The previous title.
 */
export function getPreviousTitle(
    removePrefix: RegExp = /^\/app(\/owner|\/subs|\/btadmin|\/lead\b|\/admin)?/,
    idMatch: RegExp = /^\/\d+\//,
    titleMatch: RegExp = /^\/?([a-z0-9-_]{1,240})(\/.*)?/i,
) {
    const loc = window.location;
    const locPath = loc.pathname.replace(removePrefix, "").replace(idMatch, "");
    const title = locPath.replace(titleMatch, "$1");
    return title.charAt(0).toUpperCase() + title.substring(1);
}
